/**
 * Material Ticket Card Component
 * Displays a single material ticket with details
 */

import type { MaterialTicket } from './types';

interface TicketCardProps {
  ticket: MaterialTicket;
  onDelete: (ticketId: string) => void;
}

export function TicketCard({ ticket, onDelete }: TicketCardProps) {
  const totalWeight = ticket.materials.reduce((sum, m) => sum + m.weight_tons, 0);
  const isDiverted = ticket.destination_type !== 'landfill';

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this ticket?')) {
      onDelete(ticket.id);
    }
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Hauler and Diversion Badge */}
          <div className="flex items-center gap-2 mb-1">
            <p className="font-medium text-neutral-900 truncate">{ticket.hauler_name}</p>
            <span
              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium shrink-0 ${
                isDiverted
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {isDiverted ? '✓ Diverted' : '✗ Landfill'}
            </span>
          </div>

          {/* Facility */}
          <p className="text-sm text-neutral-600 mb-2 truncate">
            → {ticket.facility_name}
          </p>

          {/* Materials */}
          <div className="flex flex-wrap gap-1 mb-2">
            {ticket.materials.map((material, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded text-xs border border-neutral-300 text-neutral-700"
              >
                {material.material_type} • {material.weight_tons}t
              </span>
            ))}
          </div>

          {/* Total Weight */}
          <p className="text-sm font-medium text-neutral-900">
            Total: {totalWeight.toFixed(1)} tons
          </p>

          {/* Notes */}
          {ticket.notes && (
            <p className="text-sm text-neutral-600 mt-2 line-clamp-2">
              {ticket.notes}
            </p>
          )}

          {/* Ticket Number */}
          {ticket.ticket_number && (
            <p className="text-xs text-neutral-500 mt-1">
              Ticket #{ticket.ticket_number}
            </p>
          )}
        </div>

        {/* Delete Action */}
        <button
          onClick={handleDelete}
          className="shrink-0 p-2 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          aria-label="Delete ticket"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}
